import { Component } from "@theme/component";
import { fetchConfig, debounce } from "@theme/utilities";
import { morphSection } from "@theme/section-renderer";
import { CartUpdateEvent } from "@theme/events";
import { storefrontEvents } from "@theme/storefront-events";

/**
 * Gift wrapping fee (cart line) + optional cart attribute `Gift note`.
 *
 * Linkage between a parent product and its gift-wrap line is done via Shopify's
 * native Nested Cart Lines API (`parent_line_key` on add, `parent_relationship`
 * on read). No custom UUID tracking.
 */
class GiftWrappingComponent extends Component {
  #debouncedMessage;

  constructor() {
    super();
    this.#debouncedMessage = debounce(this.#flushGiftMessage.bind(this), 300);
  }

  /**
   * @param {Event} event
   */
  onWholeOrderGiftToggle = async (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const wrap = /** @type {HTMLElement | undefined} */ (this.refs.wholeOrderLoadingWrap);
    wrap?.classList.add("is-loading");
    try {
      if (input.checked) {
        await this.#addWholeOrderGift(input);
      } else {
        await this.#removeAllGiftVariantLines();
      }
    } finally {
      wrap?.classList.remove("is-loading");
    }
  };

  /**
   * @param {Event} event
   */
  onGiftMessageInput = (event) => {
    this.#debouncedMessage(event);
  };

  /**
   * @param {Event} event
   */
  async #flushGiftMessage(event) {
    const target = event.target;
    if (!(target instanceof HTMLTextAreaElement)) return;

    const changes = { "Gift note": target.value };

    try {
      const cart = await storefrontEvents.trackCartRequest(
        this,
        [["CartAttributesUpdateEvent", { context: this.#eventContext, attributes: this.#mergeAttributes(changes) }]],
        async () => {
          const response = await fetch(
            FoxTheme.routes.cart_update_url,
            fetchConfig("json", { body: JSON.stringify({ attributes: changes }) })
          );
          return response.json();
        }
      );
      if (cart?.attributes) this.dataset.cartAttributes = JSON.stringify(cart.attributes);
    } catch (error) {
      console.error(error);
    }
  }

  /** @returns {"dialog" | "cart"} Standard event context of this cart surface. */
  get #eventContext() {
    return this.closest("cart-drawer-component") ? "dialog" : "cart";
  }

  /**
   * The complete attribute set the cart ends up with after `changes`, in the standard `{ key, value }` shape.
   * `update.js` merges attributes, and an empty value removes the key.
   * @param {Record<string, string>} changes
   */
  #mergeAttributes(changes) {
    let current = {};
    try {
      current = JSON.parse(this.dataset.cartAttributes || "{}") || {};
    } catch {
      current = {};
    }
    return Object.entries({ ...current, ...changes })
      .filter(([, value]) => value !== "" && value !== null && value !== undefined)
      .map(([key, value]) => ({ key, value: String(value) }));
  }

  /**
   * Sends a cart line request, reported as one `shopify:cart:lines-update`.
   * @param {string} url
   * @param {object} body
   * @param {object} linesPayload
   * @returns {Promise<any>} Parsed response.
   */
  #requestCart(url, body, linesPayload) {
    return storefrontEvents.trackCartRequest(
      this,
      [["CartLinesUpdateEvent", { ...linesPayload, context: this.#eventContext }]],
      async () => {
        const response = await fetch(url, fetchConfig("json", { body: JSON.stringify(body) }));
        return response.json();
      }
    );
  }

  get #giftVariantId() {
    return Number(this.dataset.giftVariantId);
  }

  get #syncQuantity() {
    return this.dataset.syncQuantity === "true";
  }

  #getSectionPayload() {
    const ids = [];
    document.querySelectorAll("cart-items-component").forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    });
    return {
      sections: ids.join(","),
      sections_url: window.location.pathname,
    };
  }

  /**
   * @param {object} parsed
   */
  async #applyCartJsonResponse(parsed) {
    if (!parsed || !parsed.sections) return;

    const sections = parsed.sections;
    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    let itemCount = 0;

    for (const sectionId of Object.keys(sections)) {
      await morphSection(sectionId, sections[sectionId]);
      const doc = new DOMParser().parseFromString(sections[sectionId], "text/html");
      const countEl = doc.querySelector('[ref="cartItemCount"]');
      if (countEl?.textContent) {
        itemCount = parseInt(countEl.textContent, 10) || itemCount;
      }
    }

    cartItemsComponents.forEach((comp) => {
      const sid = comp.dataset.sectionId;
      if (sid && sections[sid]) {
        comp.dispatchEvent(
          new CartUpdateEvent({}, sid, {
            itemCount,
            source: "gift-wrapping-component",
            sections,
          })
        );
      }
    });
  }

  /**
   * @returns {Promise<object>}
   */
  async #fetchCart() {
    const res = await fetch(FoxTheme.routes.cart);
    return res.json();
  }

  /**
   * Removes every cart line whose variant matches the gift fee variant (both the
   * whole-order line and any nested per-line children).
   */
  async #removeAllGiftVariantLines() {
    const variantId = this.#giftVariantId;
    while (true) {
      const cart = await this.#fetchCart();
      const item = cart.items.find((i) => Number(i.variant_id) === variantId);
      if (!item) break;

      const parsed = await this.#requestCart(
        FoxTheme.routes.cart_change_url,
        { id: item.key, quantity: 0, ...this.#getSectionPayload() },
        storefrontEvents.changeLinesPayload(item.key, 0)
      );
      // A declined removal leaves the line in place: stop rather than retry forever.
      if (storefrontEvents.getAjaxError(parsed)) break;
      await this.#applyCartJsonResponse(parsed);
    }
  }

  /**
   * @param {HTMLInputElement} wholeOrderInput
   */
  async #addWholeOrderGift(wholeOrderInput) {
    await this.#removeAllGiftVariantLines();

    let giftQuantity = 1;
    if (this.#syncQuantity) {
      const cart = await this.#fetchCart();
      giftQuantity = cart.items.reduce((sum, i) => sum + i.quantity, 0) || 1;
    }

    const items = [{ id: this.#giftVariantId, quantity: giftQuantity }];
    const parsed = await this.#requestCart(
      FoxTheme.routes.cart_add_url,
      { items, ...this.#getSectionPayload() },
      storefrontEvents.addLinesPayload(items)
    );

    if (parsed.status && parsed.message) {
      console.error(parsed.message);
      wholeOrderInput.checked = false;
      return;
    }

    await this.#applyCartJsonResponse(parsed);
  }
}

if (!customElements.get("gift-wrapping-component")) {
  customElements.define("gift-wrapping-component", GiftWrappingComponent);
}
